import type { ObservationComponent } from '@medplum/fhirtypes'
import type { ObservationTemplate } from '#src/codings.ts'
import {
  componentValue,
  withComponentValue,
} from '#src/models/observationComponents.ts'
import {
  type DerivationInputValue,
  type DerivationRule,
  derivationInputs,
  rulesForTarget,
} from '#src/observation-logic/derivations/rules.ts'
import { STARTER_DERIVATIONS } from '#src/observation-logic/derivations/starter.ts'
import { numberValue } from '#src/observation-logic/value.ts'
import { OBSERVATION_CALCULATION_EXTENSION_URL } from '#src/structureDefinitions.ts'

// Whether a part was filled in by its calculation rather than by hand.
export function isCalculatedComponent(
  component: ObservationComponent
): boolean {
  return (
    component.extension?.some(
      (extension) => extension.url === OBSERVATION_CALCULATION_EXTENSION_URL
    ) ?? false
  )
}

// The parts with every calculated part (mean arterial pressure from
// systolic and diastolic) brought up to date from the others. A part set by
// hand is kept; a calculated one whose inputs are gone is removed.
export function withDerivedComponents(
  template: ObservationTemplate,
  components: ObservationComponent[] | undefined,
  rules: readonly DerivationRule[] = STARTER_DERIVATIONS
): ObservationComponent[] {
  let next = components ?? []
  for (const spec of template.components) {
    const [rule] = rulesForTarget(spec.coding, rules)
    const inputSpecs = rule?.inputs.map((input) =>
      template.components.find(
        (candidate) =>
          candidate.coding.code === input.code &&
          candidate.coding.system === input.system
      )
    )
    if (!rule || !inputSpecs || inputSpecs.some((input) => !input)) {
      continue
    }
    const existing = next.find((component) =>
      component.code?.coding?.some(
        (coding) =>
          coding.code === spec.coding.code &&
          coding.system === spec.coding.system
      )
    )
    if (existing && !isCalculatedComponent(existing)) {
      continue
    }
    const values: DerivationInputValue[] = rule.inputs.map((coding, index) => {
      const value = componentValue({ component: next }, coding)
      const unit = inputSpecs[index]?.unit ?? undefined
      const number = typeof value === 'number' ? value : Number(value)
      return {
        coding,
        value:
          value === undefined ||
          typeof value === 'object' ||
          Number.isNaN(number)
            ? null
            : numberValue(number, unit),
      }
    })
    const result = rule.compute(derivationInputs(values, {}))
    next = withComponentValue(
      template,
      next,
      spec,
      result?.kind === 'number' ? result.value : undefined
    )
    if (result?.kind === 'number') {
      next = next.map((component) =>
        component.code?.coding?.some(
          (coding) => coding.code === spec.coding.code
        )
          ? {
              ...component,
              extension: [
                {
                  url: OBSERVATION_CALCULATION_EXTENSION_URL,
                  extension: [{ url: 'library', valueString: rule.id }],
                },
              ],
            }
          : component
      )
    }
  }
  return next
}
